/**
 * Autocorrelation-based pitch detection algorithm
 * Returns [frequency, clarity] where clarity is a measure of pitch confidence (0-1)
 */
export function detectPitch(buffer, sampleRate) {
    // Minimum and maximum frequencies we care about (roughly E2 to E6)
    const MIN_SAMPLES = Math.floor(sampleRate / 1000); // ~1000 Hz
    const MAX_SAMPLES = Math.floor(sampleRate / 80);   // ~80 Hz
    
    // Calculate RMS to detect silence
    let rms = 0;
    for (let i = 0; i < buffer.length; i++) {
        rms += buffer[i] * buffer[i];
    }
    rms = Math.sqrt(rms / buffer.length);
    
    // Silence threshold (lowered for better sensitivity)
    if (rms < 0.005) {
        return [null, 0];
    }
    
    // Autocorrelation
    const correlations = new Array(MAX_SAMPLES);
    
    for (let lag = MIN_SAMPLES; lag < MAX_SAMPLES; lag++) {
        let correlation = 0;
        for (let i = 0; i < buffer.length - lag; i++) {
            correlation += buffer[i] * buffer[i + lag];
        }
        correlations[lag] = correlation;
    }
    
    // Find the best autocorrelation peak
    let bestLag = MIN_SAMPLES;
    let bestCorrelation = correlations[MIN_SAMPLES];
    
    for (let lag = MIN_SAMPLES + 1; lag < MAX_SAMPLES; lag++) {
        if (correlations[lag] > bestCorrelation) {
            bestCorrelation = correlations[lag];
            bestLag = lag;
        }
    }
    
    // Parabolic interpolation for better accuracy
    if (bestLag > MIN_SAMPLES && bestLag < MAX_SAMPLES - 1) {
        const c0 = correlations[bestLag - 1];
        const c1 = correlations[bestLag];
        const c2 = correlations[bestLag + 1];
        
        const delta = 0.5 * (c2 - c0) / (2 * c1 - c0 - c2);
        bestLag += delta;
    }
    
    const frequency = sampleRate / bestLag;
    const clarity = bestCorrelation / (rms * rms * buffer.length);
    
    return [frequency, Math.min(clarity, 1)];
}

/**
 * Alternative: Simple zero-crossing rate detector (less accurate but faster)
 */
export function detectPitchSimple(buffer, sampleRate) {
    let crossings = 0;
    
    for (let i = 1; i < buffer.length; i++) {
        if ((buffer[i - 1] < 0 && buffer[i] >= 0) || 
            (buffer[i - 1] >= 0 && buffer[i] < 0)) {
            crossings++;
        }
    }
    
    const frequency = (crossings / 2) * (sampleRate / buffer.length);
    
    // Rough clarity estimate
    let amplitude = 0;
    for (let i = 0; i < buffer.length; i++) {
        amplitude += Math.abs(buffer[i]);
    }
    amplitude /= buffer.length;
    
    const clarity = Math.min(amplitude * 10, 1);
    
    return [frequency, clarity];
}
